/**
 * Saisie Rapide pour Relevés de Devoirs (relevés à mauvaise qualité OCR).
 *
 * Workflow :
 *  1. Liste des relevés existants (avec barre de recherche + filtres)
 *  2. Clic sur un relevé → vue saisie :
 *     - GAUCHE : pages du PDF scanné (rendues via react-pdf, navigation page par page)
 *     - DROITE : étudiant courant + notes D1/D2 + barre de progression
 *  3. Saisie manuelle assistée (l'opérateur lit le nom à gauche, tape les notes à droite)
 *  4. Bouton "Tout enregistrer" → batch via /notes-devoirs/saisie_matricielle/
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  ChevronLeft, ChevronRight, Search, ZoomIn, ZoomOut,
  CheckCircle2, Save, FileText, Loader, ArrowRight,
  X, AlertCircle, Filter, Users, Zap, Target, Keyboard,
  Copy as CopyIcon, Upload, Eye,
} from 'lucide-react';
import {
  useGetRelevesDevoirsQuery,
  useGetReleveDevoirsByIdQuery,
  useUploadScanDevoirsMutation,
} from '../../services/ocrApi';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { typeFormationForOption } from '../../utils/typeFormation';
import { useSaisieMatricielleMutation } from '../../services/notesDevoirsApi';
import { API_URL } from '../../services/baseApi';

// Worker PDF.js servi depuis public/ (pas de CDN, fonctionne offline).
// Le ?v= force le navigateur à re-télécharger après la correction du MIME type
// nginx (incident 2026-05-03 : .mjs servi en application/octet-stream au lieu de
// text/javascript, navigateur refuse le module → cache du mauvais Content-Type).
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs?v=2';

interface NoteEntry {
  d1: number | null;
  d2: number | null;
  absent: boolean;
  modified: boolean;
}

const SaisieRapideRelevesDevoirs: React.FC = () => {
  const navigate = useNavigate();

  // ─── Étape principale ─────────────────────────────────────────────────────
  const [etape, setEtape] = useState<'liste' | 'saisie'>('liste');
  const [selectedReleveId, setSelectedReleveId] = useState<number | null>(null);

  // ─── VUE LISTE ────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterNiveau, setFilterNiveau] = useState('');
  const [filterSemestre, setFilterSemestre] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  const { data: releves = [], isLoading: loadingReleves } = useGetRelevesDevoirsQuery();

  const filteredReleves = useMemo(() => {
    let list = [...releves];
    if (filterNiveau) list = list.filter(r => r.niveau === filterNiveau);
    if (filterSemestre) list = list.filter(r => r.semestre === filterSemestre);
    if (filterStatut) list = list.filter(r => r.status === filterStatut);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(r =>
        (r.code_releve || '').toLowerCase().includes(q) ||
        (r.matiere_nom || '').toLowerCase().includes(q) ||
        (r.matiere_code || '').toLowerCase().includes(q) ||
        (r.option || '').toLowerCase().includes(q) ||
        (r.enseignant_nom || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => (b.date_generation || '').localeCompare(a.date_generation || ''));
    return list;
  }, [releves, search, filterNiveau, filterSemestre, filterStatut]);

  // ─── VUE SAISIE ───────────────────────────────────────────────────────────
  const { data: releve, isLoading: loadingReleve } = useGetReleveDevoirsByIdQuery(
    selectedReleveId || 0,
    { skip: !selectedReleveId }
  );

  const { data: etudiants = [], isLoading: loadingEtudiants } = useGetEtudiantsQuery(
    releve ? {
      niveau: releve.niveau,
      option: releve.option,
      // Option dont le nom finit par "(CS)" → étudiants CS, sinon FI + DD.
      type_formation: typeFormationForOption(releve.option),
      actif: true,
    } : {},
    { skip: !releve }
  );

  // Trier les étudiants par nom puis prénoms (= ordre du relevé physique)
  const etudiantsTries = useMemo(() => {
    return [...etudiants].sort((a, b) => {
      const cmp = (a.nom || '').localeCompare(b.nom || '', 'fr');
      if (cmp !== 0) return cmp;
      return (a.prenoms || '').localeCompare(b.prenoms || '', 'fr');
    });
  }, [etudiants]);

  // État des notes saisies (en mémoire local jusqu'au "Tout enregistrer")
  const [notes, setNotes] = useState<Record<number, NoteEntry>>({});
  const [etudiantIndex, setEtudiantIndex] = useState(0);

  const etudiantCourant = etudiantsTries[etudiantIndex];

  // Inputs courants
  const [inputD1, setInputD1] = useState('');
  const [inputD2, setInputD2] = useState('');
  const [absent, setAbsent] = useState(false);

  const d1Ref = useRef<HTMLInputElement>(null);
  const d2Ref = useRef<HTMLInputElement>(null);

  // Charger les valeurs en mémoire quand on change d'étudiant
  useEffect(() => {
    if (!etudiantCourant) return;
    const n = notes[etudiantCourant.id];
    setInputD1(n?.d1 != null ? String(n.d1) : '');
    setInputD2(n?.d2 != null ? String(n.d2) : '');
    setAbsent(!!n?.absent);
    // Focus auto sur D1
    const t = setTimeout(() => {
      d1Ref.current?.focus();
      d1Ref.current?.select();
    }, 30);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etudiantCourant?.id]);

  // Reset l'index quand on change de relevé
  useEffect(() => {
    setEtudiantIndex(0);
    setNotes({});
  }, [selectedReleveId]);

  // Persister la saisie courante (avant navigation)
  const persistCurrent = useCallback(() => {
    if (!etudiantCourant) return;
    const d1 = absent ? 0 : (inputD1.trim() ? parseFloat(inputD1) : null);
    const d2 = absent ? 0 : (inputD2.trim() ? parseFloat(inputD2) : null);
    // Ne sauvegarde que s'il y a au moins une valeur (évite d'écraser avec rien)
    if (d1 == null && d2 == null && !absent) {
      // Si le user n'a rien entré, on retire l'entrée existante
      setNotes(prev => {
        const next = { ...prev };
        delete next[etudiantCourant.id];
        return next;
      });
      return;
    }
    setNotes(prev => ({
      ...prev,
      [etudiantCourant.id]: { d1, d2, absent, modified: true },
    }));
  }, [etudiantCourant, absent, inputD1, inputD2]);

  const goToIndex = useCallback((idx: number) => {
    persistCurrent();
    if (idx >= 0 && idx < etudiantsTries.length) {
      setEtudiantIndex(idx);
    }
  }, [persistCurrent, etudiantsTries.length]);

  const goNext = useCallback(() => goToIndex(etudiantIndex + 1), [goToIndex, etudiantIndex]);
  const goPrev = useCallback(() => goToIndex(etudiantIndex - 1), [goToIndex, etudiantIndex]);

  // Copier D1 → D2
  const copyD1ToD2 = () => {
    setInputD2(inputD1);
    setTimeout(() => d2Ref.current?.focus(), 0);
  };

  // ─── PDF ──────────────────────────────────────────────────────────────────
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1.2);

  const scanUrl = useMemo(() => {
    if (!releve?.fichier_scan) return null;
    if (releve.fichier_scan.startsWith('http')) return releve.fichier_scan;
    return `${API_URL.replace('/api', '')}${releve.fichier_scan.startsWith('/') ? '' : '/'}${releve.fichier_scan}`;
  }, [releve]);

  // Stable file object pour éviter les rerenders du PDF (option Document)
  const pdfFile = useMemo(() => (scanUrl ? { url: scanUrl } : null), [scanUrl]);

  // ─── UPLOAD SCAN ──────────────────────────────────────────────────────────
  const [uploadScan, { isLoading: isUploading }] = useUploadScanDevoirsMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadScan = async (file: File) => {
    if (!releve) return;
    if (!file.name.toLowerCase().match(/\.(pdf|jpg|jpeg|png|tiff)$/)) {
      setSaveMessage({ type: 'error', text: 'Format non supporté (PDF, JPG, PNG, TIFF uniquement)' });
      return;
    }
    const formData = new FormData();
    formData.append('fichier', file);
    try {
      await uploadScan({ releveId: releve.id, formData }).unwrap();
      setSaveMessage({ type: 'success', text: `Scan « ${file.name} » uploadé avec succès ✓` });
    } catch (err: any) {
      setSaveMessage({ type: 'error', text: err?.data?.error || 'Erreur lors de l\'upload du scan' });
    }
  };

  // ─── SAUVEGARDE BATCH ─────────────────────────────────────────────────────
  const [saveMatricielle, { isLoading: isSaving }] = useSaisieMatricielleMutation();
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSaveAll = async () => {
    persistCurrent();
    if (!releve) return;

    // Construire le payload depuis l'état notes (après que persistCurrent ait commit)
    // → on attend un tick pour que setNotes ait pris effet
    setTimeout(async () => {
      const notesToSave = Object.entries(notes)
        .filter(([_, n]) => n.modified)
        .map(([etudiantId, n]) => ({
          etudiant_id: parseInt(etudiantId),
          note1: n.d1,
          absent1: n.absent,
          note2: n.d2,
          absent2: n.absent,
        }));

      if (notesToSave.length === 0) {
        setSaveMessage({ type: 'error', text: 'Aucune note saisie à enregistrer' });
        return;
      }

      if (!window.confirm(`Enregistrer ${notesToSave.length} étudiant(s) en base ?`)) return;

      try {
        const matiereId = typeof releve.matiere === 'object'
          ? (releve.matiere as any).id
          : releve.matiere;
        await saveMatricielle({
          matiere_id: matiereId,
          annee_academique: releve.annee_academique,
          semestre: releve.semestre,
          notes: notesToSave,
        }).unwrap();
        setSaveMessage({ type: 'success', text: `${notesToSave.length} note(s) enregistrée(s) avec succès ✓` });
      } catch (err: any) {
        setSaveMessage({ type: 'error', text: err?.data?.error || 'Erreur lors de la sauvegarde' });
      }
    }, 30);
  };

  // ─── KEYBOARD ─────────────────────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'd1' | 'd2') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (field === 'd1') {
        // Si D2 est vide, focus dessus ; sinon → suivant
        if (!inputD2.trim()) {
          d2Ref.current?.focus();
          d2Ref.current?.select();
        } else {
          goNext();
        }
      } else {
        goNext();
      }
    } else if (e.key === 'Tab' && !e.shiftKey && field === 'd1') {
      // Tab depuis D1 → D2 (comportement natif déjà OK, mais on s'assure)
      // pas de preventDefault, laisser le navigateur faire
    } else if (e.altKey && e.key === 'ArrowDown') {
      e.preventDefault();
      goNext();
    } else if (e.altKey && e.key === 'ArrowUp') {
      e.preventDefault();
      goPrev();
    } else if ((e.key === 'c' || e.key === 'C' || e.key === ' ') && inputD1.trim() && !absent) {
      // Raccourci : C ou Espace → copie D1 dans D2 + valide + passe au suivant.
      // Les inputs sont type="number" donc 'c'/' ' ne produisent aucune saisie utile,
      // on peut les capturer sans gêner la saisie normale.
      e.preventDefault();
      const d1Val = parseFloat(inputD1);
      if (Number.isNaN(d1Val) || !etudiantCourant) return;
      // Persist directement (bypass de persistCurrent qui lit depuis state async)
      setInputD2(inputD1);
      setNotes(prev => ({
        ...prev,
        [etudiantCourant.id]: { d1: d1Val, d2: d1Val, absent: false, modified: true },
      }));
      // Avancer au suivant
      if (etudiantIndex < etudiantsTries.length - 1) {
        setEtudiantIndex(etudiantIndex + 1);
      }
    }
  };

  // ─── COMPTEURS ────────────────────────────────────────────────────────────
  const nbSaisies = Object.values(notes).filter(n => n.modified).length;
  const progression = etudiantsTries.length > 0 ? (nbSaisies / etudiantsTries.length) * 100 : 0;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER : VUE LISTE
  // ─────────────────────────────────────────────────────────────────────────
  if (etape === 'liste') {
    const niveauxDispo = Array.from(new Set(releves.map(r => r.niveau).filter(Boolean))).sort();
    const statutsDispo: Array<{ value: string; label: string; color: string }> = [
      { value: 'genere', label: 'Généré', color: 'bg-gray-100 text-gray-700' },
      { value: 'imprime', label: 'Imprimé', color: 'bg-blue-100 text-blue-700' },
      { value: 'rempli', label: 'Rempli', color: 'bg-purple-100 text-purple-700' },
      { value: 'scanne', label: 'Scanné', color: 'bg-indigo-100 text-indigo-700' },
      { value: 'traite', label: 'Traité OCR', color: 'bg-yellow-100 text-yellow-700' },
      { value: 'verifie', label: 'Vérifié', color: 'bg-orange-100 text-orange-700' },
      { value: 'valide', label: 'Validé', color: 'bg-green-100 text-green-700' },
    ];
    const statutLabel = (s: string) => statutsDispo.find(x => x.value === s) || { label: s, color: 'bg-gray-100 text-gray-700' };

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/saisie-notes-devoirs')}
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-2 mb-3 text-sm"
            >
              <ChevronLeft size={18} /> Retour
            </button>
            <div className="flex items-center gap-3 mb-1">
              <Zap className="text-green-600" size={28} />
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                Saisie Rapide — Relevés Devoirs
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Choisissez un relevé pour saisir les notes manuellement à partir du scan
            </p>
          </div>

          {/* Filtres + recherche */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[260px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher par code, matière, classe, formateur…"
                  className="w-full pl-10 pr-9 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Filter size={14} className="text-gray-400" />
                <select
                  value={filterNiveau}
                  onChange={e => setFilterNiveau(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Tous niveaux</option>
                  {niveauxDispo.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <select
                  value={filterSemestre}
                  onChange={e => setFilterSemestre(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">S1 + S2</option>
                  <option value="S1">S1</option>
                  <option value="S2">S2</option>
                </select>
                <select
                  value={filterStatut}
                  onChange={e => setFilterStatut(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Tous statuts</option>
                  {statutsDispo.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
              {filteredReleves.length} / {releves.length} relevé(s)
            </div>
          </div>

          {/* Liste des relevés */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {loadingReleves ? (
              <div className="p-12 text-center text-gray-500">
                <Loader className="animate-spin mx-auto mb-3" size={28} />
                Chargement…
              </div>
            ) : filteredReleves.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <FileText size={40} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">{search || filterNiveau || filterSemestre || filterStatut
                  ? 'Aucun relevé ne correspond aux filtres'
                  : 'Aucun relevé disponible'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/40 text-xs text-gray-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Code</th>
                      <th className="px-3 py-2 text-left font-semibold">Matière</th>
                      <th className="px-3 py-2 text-left font-semibold">Classe</th>
                      <th className="px-3 py-2 text-center font-semibold">Sem.</th>
                      <th className="px-3 py-2 text-left font-semibold">Formateur</th>
                      <th className="px-3 py-2 text-center font-semibold">Étudiants</th>
                      <th className="px-3 py-2 text-center font-semibold">Statut</th>
                      <th className="px-3 py-2 text-left font-semibold">Date</th>
                      <th className="px-3 py-2 text-center font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filteredReleves.map(r => {
                      const st = statutLabel(r.status);
                      const aScan = !!r.fichier_scan;
                      return (
                        <tr key={r.id} className="hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors">
                          <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{r.code_releve}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-900 dark:text-gray-100 text-xs">{r.matiere_nom}</div>
                            <div className="text-[10px] text-gray-400">{r.matiere_code}</div>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                            <span className="font-bold">{r.niveau}</span> {r.option}
                          </td>
                          <td className="px-3 py-2 text-center text-xs">{r.semestre}</td>
                          <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">{r.enseignant_nom || '—'}</td>
                          <td className="px-3 py-2 text-center text-xs font-bold">{r.nombre_etudiants}</td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${st.color}`}>
                                {st.label}
                              </span>
                              {!aScan && (
                                <span className="inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700" title="Aucun scan uploadé">
                                  ⚠ Sans scan
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-[11px] text-gray-500">
                            {r.date_generation ? new Date(r.date_generation).toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => {
                                setSelectedReleveId(r.id);
                                setEtape('saisie');
                                setSaveMessage(null);
                              }}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 text-white text-xs font-semibold rounded ${
                                aScan
                                  ? 'bg-green-600 hover:bg-green-700'
                                  : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                              title={aScan ? 'Saisir avec scan disponible' : 'Uploader le scan puis saisir'}
                            >
                              {aScan ? <Zap size={12} /> : <Upload size={12} />}
                              {aScan ? 'Saisir' : 'Upload + Saisir'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER : VUE SAISIE
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Header sticky */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-2 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => {
                if (nbSaisies > 0 && !window.confirm(`${nbSaisies} note(s) saisie(s) non enregistrée(s). Quitter ?`)) return;
                setEtape('liste');
                setSelectedReleveId(null);
              }}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
                {releve?.code_releve} · {releve?.matiere_nom}
              </h1>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {releve?.niveau} {releve?.option} · {releve?.semestre} · {releve?.enseignant_nom || 'sans formateur'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs">
              <Target size={14} className="text-green-600" />
              <span className="font-semibold dark:text-gray-200">{nbSaisies}</span>
              <span className="text-gray-500">/ {etudiantsTries.length}</span>
            </div>
            <button
              onClick={handleSaveAll}
              disabled={isSaving || nbSaisies === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? <Loader size={12} className="animate-spin" /> : <Save size={12} />}
              Tout enregistrer
            </button>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
          <div
            className="bg-gradient-to-r from-green-500 to-blue-500 h-full rounded-full transition-all"
            style={{ width: `${progression}%` }}
          />
        </div>

        {/* Message */}
        {saveMessage && (
          <div className={`mt-2 px-3 py-1.5 rounded text-xs flex items-center justify-between ${
            saveMessage.type === 'success'
              ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
              : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
          }`}>
            <span>{saveMessage.text}</span>
            <button onClick={() => setSaveMessage(null)}><X size={12} /></button>
          </div>
        )}
      </div>

      {/* Body : 3 colonnes — PDF | Saisie | Sidebar étudiants */}
      <div className="flex-1 flex overflow-hidden">
        {/* ─── GAUCHE : PDF ─────────────────────────────────────────────── */}
        <div className="flex-1 bg-gray-800 dark:bg-gray-950 flex flex-col overflow-hidden">
          {/* Toolbar PDF */}
          <div className="px-3 py-2 bg-gray-900 flex items-center justify-between gap-2 text-white border-b border-gray-700">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                disabled={pageNumber <= 1}
                className="p-1 rounded hover:bg-white/10 disabled:opacity-30"
                title="Page précédente"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs tabular-nums px-1">
                Page <strong>{pageNumber}</strong> / {numPages || '?'}
              </span>
              <button
                onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                disabled={pageNumber >= numPages}
                className="p-1 rounded hover:bg-white/10 disabled:opacity-30"
                title="Page suivante"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setPdfScale(Math.max(0.6, pdfScale - 0.2))} className="p-1 rounded hover:bg-white/10" title="Zoom -">
                <ZoomOut size={14} />
              </button>
              <span className="text-[11px] tabular-nums w-10 text-center">{Math.round(pdfScale * 100)}%</span>
              <button onClick={() => setPdfScale(Math.min(2.5, pdfScale + 0.2))} className="p-1 rounded hover:bg-white/10" title="Zoom +">
                <ZoomIn size={14} />
              </button>
              {pdfFile && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="ml-2 px-2 py-1 text-[10px] bg-amber-600 hover:bg-amber-700 text-white rounded flex items-center gap-1"
                  title="Remplacer le scan actuel"
                  disabled={isUploading}
                >
                  {isUploading ? <Loader size={11} className="animate-spin" /> : <Upload size={11} />}
                  Remplacer
                </button>
              )}
            </div>
          </div>

          {/* File input partagé (zone upload + bouton "Remplacer") */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.tiff"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUploadScan(f);
              e.target.value = '';
            }}
            className="hidden"
          />

          {/* Viewer PDF / Zone upload */}
          <div className="flex-1 overflow-auto p-4 flex items-start justify-center">
            {loadingReleve ? (
              <div className="text-white/60 text-sm flex items-center gap-2">
                <Loader className="animate-spin" size={20} /> Chargement du relevé…
              </div>
            ) : !pdfFile ? (
              /* Zone d'upload — affichée quand le relevé n'a pas encore de scan */
              <div className="w-full max-w-md mt-12">
                <div
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleUploadScan(f);
                  }}
                  className="border-2 border-dashed border-white/30 rounded-xl p-8 text-center bg-white/5 hover:bg-white/10 transition-colors"
                >
                  {isUploading ? (
                    <div className="text-white flex flex-col items-center gap-3">
                      <Loader className="animate-spin" size={40} />
                      <p className="text-sm">Upload en cours…</p>
                    </div>
                  ) : (
                    <>
                      <Upload size={48} className="mx-auto text-white/60 mb-4" />
                      <p className="text-white font-semibold mb-1">Aucun scan uploadé pour ce relevé</p>
                      <p className="text-white/60 text-xs mb-4">
                        Glissez-déposez le fichier ici, ou cliquez pour choisir
                      </p>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded text-sm"
                      >
                        <Upload size={16} /> Choisir un fichier
                      </button>
                      <p className="text-white/40 text-[10px] mt-3">
                        Formats acceptés : PDF, JPG, PNG, TIFF
                      </p>
                      <p className="text-white/40 text-[10px] mt-2">
                        💡 Une fois uploadé, vous pourrez lire les noms à gauche et saisir à droite
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <Document
                file={pdfFile}
                onLoadSuccess={({ numPages }) => { setNumPages(numPages); setPageNumber(1); }}
                loading={<div className="text-white/60 text-sm flex items-center gap-2"><Loader className="animate-spin" size={20} /> Chargement du PDF…</div>}
                error={<div className="text-red-400 text-sm">Erreur de chargement du PDF</div>}
                noData={<div className="text-white/60 text-sm">Aucun scan</div>}
              >
                <Page
                  pageNumber={pageNumber}
                  scale={pdfScale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-2xl"
                  loading={<div className="text-white/40 text-xs p-8">Rendu page {pageNumber}…</div>}
                />
              </Document>
            )}
          </div>
        </div>

        {/* ─── MILIEU : Saisie ──────────────────────────────────────────── */}
        <div className="w-[420px] bg-white dark:bg-gray-800 border-l dark:border-gray-700 flex flex-col overflow-hidden">
          {loadingEtudiants ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <Loader className="animate-spin mr-2" size={20} /> Chargement des étudiants…
            </div>
          ) : etudiantsTries.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm p-6 text-center">
              Aucun étudiant trouvé pour {releve?.niveau} {releve?.option}
            </div>
          ) : etudiantCourant ? (
            <>
              {/* Étudiant courant — gros et lisible */}
              <div className="px-5 py-4 border-b dark:border-gray-700 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800">
                <div className="text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-400 font-bold mb-1">
                  Étudiant {etudiantIndex + 1} sur {etudiantsTries.length}
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 dark:text-gray-100 leading-tight">
                  {etudiantCourant.nom}
                </h2>
                <p className="text-base text-gray-700 dark:text-gray-300 mt-0.5">
                  {etudiantCourant.prenoms}
                </p>
                <p className="text-[11px] font-mono text-gray-500 mt-1">
                  {etudiantCourant.matricule}
                </p>
              </div>

              {/* Champs */}
              <div className="px-5 py-4 space-y-4 flex-1 overflow-y-auto">
                {/* Absent */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={absent}
                    onChange={e => setAbsent(e.target.checked)}
                    className="w-4 h-4 text-orange-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Absent (D1 et D2 = 0)
                  </span>
                </label>

                {/* D1 / D2 inputs */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">
                      Devoir 1 /20
                    </label>
                    <input
                      ref={d1Ref}
                      type="number"
                      step="0.25"
                      min="0"
                      max="20"
                      value={inputD1}
                      onChange={e => setInputD1(e.target.value)}
                      onKeyDown={e => handleKeyDown(e, 'd1')}
                      disabled={absent}
                      className="w-full px-3 py-3 text-2xl font-bold text-center border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-40"
                      placeholder="—"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
                      Devoir 2 /20
                    </label>
                    <input
                      ref={d2Ref}
                      type="number"
                      step="0.25"
                      min="0"
                      max="20"
                      value={inputD2}
                      onChange={e => setInputD2(e.target.value)}
                      onKeyDown={e => handleKeyDown(e, 'd2')}
                      disabled={absent}
                      className="w-full px-3 py-3 text-2xl font-bold text-center border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-40"
                      placeholder="—"
                    />
                  </div>
                </div>

                {/* Bouton D1 → D2 */}
                <button
                  onClick={copyD1ToD2}
                  disabled={absent || !inputD1.trim()}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/60 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CopyIcon size={12} /> Copier D1 vers D2
                </button>

                {/* Boutons navigation */}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <button
                    onClick={goPrev}
                    disabled={etudiantIndex === 0}
                    className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 disabled:opacity-40"
                  >
                    <ChevronLeft size={14} /> Précédent
                  </button>
                  <button
                    onClick={goNext}
                    disabled={etudiantIndex === etudiantsTries.length - 1}
                    className="flex items-center justify-center gap-1 px-3 py-3 text-sm font-bold bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed shadow"
                  >
                    Suivant <ArrowRight size={14} />
                  </button>
                </div>

                {/* Raccourcis clavier */}
                <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700/40 rounded text-[10px] text-gray-600 dark:text-gray-400 space-y-0.5">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <Keyboard size={11} /> Raccourcis
                  </div>
                  <div>
                    <kbd className="px-1 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 rounded shadow-sm text-[9px] font-bold">C</kbd>
                    {' '}ou{' '}
                    <kbd className="px-1 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 rounded shadow-sm text-[9px] font-bold">Espace</kbd>
                    {' '}→ copie D1→D2 + valide + suivant
                  </div>
                  <div><kbd className="px-1 py-0.5 bg-white dark:bg-gray-600 rounded shadow-sm text-[9px]">Enter</kbd> dans D1 → focus D2 (ou suivant si D2 rempli)</div>
                  <div><kbd className="px-1 py-0.5 bg-white dark:bg-gray-600 rounded shadow-sm text-[9px]">Enter</kbd> dans D2 → étudiant suivant</div>
                  <div><kbd className="px-1 py-0.5 bg-white dark:bg-gray-600 rounded shadow-sm text-[9px]">Tab</kbd> → champ suivant</div>
                  <div><kbd className="px-1 py-0.5 bg-white dark:bg-gray-600 rounded shadow-sm text-[9px]">Alt+↓</kbd> / <kbd className="px-1 py-0.5 bg-white dark:bg-gray-600 rounded shadow-sm text-[9px]">Alt+↑</kbd> navigation rapide</div>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* ─── DROITE : Sidebar liste étudiants ─────────────────────────── */}
        <div className="w-56 bg-gray-50 dark:bg-gray-900 border-l dark:border-gray-700 overflow-y-auto shrink-0">
          <div className="sticky top-0 bg-gray-100 dark:bg-gray-800 px-3 py-2 border-b dark:border-gray-700 z-10">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1">
              <Users size={11} /> Liste ({etudiantsTries.length})
            </div>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {etudiantsTries.map((e, idx) => {
              const isSel = idx === etudiantIndex;
              const note = notes[e.id];
              const hasNote = !!(note?.modified);
              return (
                <button
                  key={e.id}
                  onClick={() => goToIndex(idx)}
                  className={`w-full text-left px-2.5 py-1.5 text-[11px] transition-colors ${
                    isSel
                      ? 'bg-blue-100 dark:bg-blue-900/30 border-l-4 border-blue-500'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 border-l-4 border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-gray-400 tabular-nums w-5 shrink-0">{idx + 1}</span>
                    <span className={`flex-1 truncate ${isSel ? 'font-bold text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                      {e.nom}
                    </span>
                    {hasNote ? (
                      <CheckCircle2 size={10} className="text-green-500 shrink-0" />
                    ) : (
                      <span className="w-2.5 h-2.5 shrink-0" />
                    )}
                  </div>
                  {hasNote && (
                    <div className="pl-7 text-[9px] text-gray-500 mt-0.5">
                      {note.absent ? 'AB' : `D1: ${note.d1 ?? '—'} · D2: ${note.d2 ?? '—'}`}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaisieRapideRelevesDevoirs;
